import { useState, useEffect } from 'react';
import {
  Plus,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  X,
  Clock,
} from 'lucide-react';
import { credits, CreditTransaction } from '../../api/client';
import { formatNumber, cn } from '../../lib/utils';

const AMOUNT_PRESETS = [100, 500, 1000];

const REASON_OPTIONS = [
  { value: 'test_allocation', label: 'Test Allocation' },
  { value: 'beta_reward', label: 'Beta Reward' },
  { value: 'bug_compensation', label: 'Bug Compensation' },
  { value: 'manual_topup', label: 'Manual Top-up' },
];

export default function CreditsGrant() {
  const [deviceId, setDeviceId] = useState('');
  const [amount, setAmount] = useState<number>(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [reason, setReason] = useState('test_allocation');
  const [customReason, setCustomReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ balance: number; deviceId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recentGrants, setRecentGrants] = useState<CreditTransaction[]>([]);
  const [loadingGrants, setLoadingGrants] = useState(true);
  const [pendingGrant, setPendingGrant] = useState<{
    deviceId: string;
    amount: number;
    reason: string;
    note: string;
  } | null>(null);

  const fetchRecentGrants = async () => {
    // There's no global "grants" endpoint, so we skip if no device ID
    // We'll show the most recent grant activity after submission
    setLoadingGrants(false);
  };

  useEffect(() => {
    fetchRecentGrants();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const finalDeviceId = deviceId.trim();
    const finalAmount = useCustom ? Number(customAmount.trim()) : amount;
    const finalReason = (reason === 'custom' ? customReason : reason).trim();
    const finalNote = note.trim();

    if (!finalDeviceId) {
      setError('Device ID is required');
      return;
    }
    if (!Number.isFinite(finalAmount) || !Number.isSafeInteger(finalAmount) || finalAmount <= 0) {
      setError('Amount must be a positive whole number');
      return;
    }
    if (!finalReason) {
      setError('Reason is required');
      return;
    }

    setPendingGrant({
      deviceId: finalDeviceId,
      amount: finalAmount,
      reason: finalReason,
      note: finalNote,
    });
  };

  const confirmGrant = async () => {
    if (!pendingGrant || submitting) return;

    const grant = pendingGrant;
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const res = await credits.grant({
        device_id: grant.deviceId,
        amount: grant.amount,
        reason: grant.reason,
        note: grant.note || undefined,
      });
      setSuccess({ balance: res.new_balance, deviceId: res.device_id });

      // Fetch updated history for this device
      try {
        const histRes = await credits.history(grant.deviceId, { type: 'grant', limit: 10 });
        setRecentGrants(histRes.transactions);
      } catch {
        // Non-critical
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Grant failed');
    } finally {
      setSubmitting(false);
      setPendingGrant(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-3xl font-bold text-gradient">Grant Credits</h1>
        <p className="text-muted-foreground mt-1">Manually grant credits to a device</p>
      </div>

      {/* Success Banner */}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-emerald-400">Credits granted successfully</p>
              <p className="text-sm text-emerald-300/80 mt-1">
                New balance for {success.deviceId.slice(0, 12)}...: <span className="font-mono font-bold">{formatNumber(success.balance)}</span>
              </p>
            </div>
            <button onClick={() => setSuccess(null)} className="p-1 rounded-lg hover:bg-emerald-500/20 text-emerald-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-rose-300/80 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="p-1 rounded-lg hover:bg-rose-500/20 text-rose-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Grant Form */}
        <form onSubmit={handleSubmit} className="card-glow rounded-2xl p-6 space-y-6 animate-fade-in-up animate-delay-100">
          {/* Device ID */}
          <div>
            <label className="block text-sm font-medium mb-2">Device ID</label>
            <input
              type="text"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="Paste full UUID..."
              className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium mb-2">Amount</label>
            <div className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                {AMOUNT_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => { setAmount(preset); setUseCustom(false); }}
                    className={cn(
                      'px-4 py-2 rounded-xl text-sm font-medium transition-all',
                      !useCustom && amount === preset
                        ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {formatNumber(preset)}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setUseCustom(true)}
                  className={cn(
                    'px-4 py-2 rounded-xl text-sm font-medium transition-all',
                    useCustom
                      ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40'
                      : 'bg-secondary text-muted-foreground hover:text-foreground'
                  )}
                >
                  Custom
                </button>
              </div>
              {useCustom && (
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Enter amount..."
                  min="1"
                  className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                />
              )}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium mb-2">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
            >
              {REASON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
              <option value="custom">Custom...</option>
            </select>
            {reason === 'custom' && (
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter reason..."
                className="w-full mt-2 px-4 py-2.5 rounded-xl bg-secondary border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
              />
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium mb-2">Note <span className="text-muted-foreground">(optional)</span></label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Admin note..."
              className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || pendingGrant !== null}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {submitting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : pendingGrant ? (
              <AlertCircle className="w-4 h-4" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {submitting ? 'Granting...' : pendingGrant ? 'Confirm Pending Grant' : 'Grant Credits'}
          </button>
        </form>

        {/* Recent Grants */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-200">
          <h2 className="text-lg font-semibold mb-4">Recent Grants</h2>
          {recentGrants.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mx-auto mb-3">
                <Plus className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">
                {loadingGrants ? 'Loading...' : 'Grant credits to see history here'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentGrants.map((tx) => (
                <div key={tx.id} className="flex items-center gap-3 text-sm py-2.5 px-3 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-2 text-muted-foreground w-20">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-xs">
                      {new Date(tx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <code className="text-xs text-muted-foreground font-mono">
                    {tx.device_id.slice(0, 8)}...
                  </code>
                  <span className="font-mono font-medium text-emerald-400 ml-auto">
                    +{formatNumber(tx.amount)}
                  </span>
                  <span className="text-xs text-muted-foreground/70 w-20 text-right truncate">
                    {tx.reason || ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {pendingGrant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="card-glow rounded-2xl p-6 w-full max-w-lg border border-emerald-500/20 animate-scale-in">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Confirm Credit Grant</h2>
                <p className="text-sm text-muted-foreground mt-1">This will grant credits immediately.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Device ID</p>
                <div className="rounded-xl bg-secondary/60 border border-border/50 p-3 font-mono text-xs break-all">
                  {pendingGrant.deviceId}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Amount</p>
                  <p className="font-mono text-emerald-400 font-semibold">{formatNumber(pendingGrant.amount)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Reason</p>
                  <p className="text-sm break-all">{pendingGrant.reason}</p>
                </div>
              </div>
              {pendingGrant.note && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Note</p>
                  <p className="text-sm text-muted-foreground break-all">{pendingGrant.note}</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setPendingGrant(null)}
                disabled={submitting}
                className="btn-secondary px-4 py-2 rounded-xl text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmGrant}
                disabled={submitting}
                className="px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Confirm Grant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
